import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";
import { createDrawerNavigator } from "@react-navigation/drawer";
import { DarkTheme, DefaultTheme, NavigationContainer, NavigationIndependentTree } from "@react-navigation/native";
import { PropsWithChildren, useRef } from "react";

const Drawer = createDrawerNavigator();

interface DrawerNavigatorProps extends PropsWithChildren {
  initialRouteName?: string;
  onScreenChange?: (screenName: string) => void;
  /** Entity type for tracking first visit (e.g., "client", "project", "shift", "user") */
  entityType?: string;
}

export default function DrawerNavigator({
  children,
  initialRouteName,
  onScreenChange,
  entityType,
}: DrawerNavigatorProps) {
  const { colors, theme } = useTheme();
  const { getBoolean, saveFast } = useStorage();

  // Check if this entity type's drawer has been visited (user selected an item)
  const storageKey = entityType ? `drawer_visited_${entityType}` : null;
  const hasVisited = storageKey ? getBoolean(storageKey) === true : true;

  // Track if we've already marked as visited this session to avoid repeated saves
  const markedVisitedRef = useRef(hasVisited);

  // Mark as visited when user selects a screen
  const handleScreenFocus = (screenName: string) => {
    // Mark as visited on first screen selection
    if (storageKey && !markedVisitedRef.current) {
      saveFast(storageKey, true);
      markedVisitedRef.current = true;
    }
    onScreenChange?.(screenName);
  };

  return (
    <NavigationIndependentTree>
      <NavigationContainer
        documentTitle={{ enabled: false }}
        theme={{
          dark: theme.name === "dark",
          fonts: theme.name === "dark" ? DarkTheme.fonts : DefaultTheme.fonts,
          colors: {
            background: theme.card,
            border: theme.border,
            card: theme.card,
            primary: theme.primary,
            text: theme.text,
            notification: theme.name === "dark" ? DarkTheme.colors.notification : DefaultTheme.colors.notification,
          },
        }}
      >
        <Drawer.Navigator
          initialRouteName={initialRouteName}
          defaultStatus={hasVisited ? "closed" : "open"}
          screenListeners={({ route }) => ({
            focus: () => handleScreenFocus(route.name.toLowerCase()),
          })}
          screenOptions={{
            headerShown: true,
            drawerStyle: {
              backgroundColor: colors.backgroundSecondary,
              width: 240,
            },
            drawerLabelStyle: { color: colors.text },
            drawerActiveTintColor: colors.primary,
            drawerInactiveTintColor: colors.text,
            drawerActiveBackgroundColor: colors.backgroundSecondary,
            drawerItemStyle: { borderRadius: 12 },
            headerTintColor: colors.text,
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: colors.backgroundSecondary,
            },
            headerTitleStyle: {
              borderColor: colors.border,
              color: colors.text,
            },
            drawerType: "back",
            sceneStyle: {
              backgroundColor: colors.backgroundSecondary,
            },
          }}
        >
          {children}
        </Drawer.Navigator>
      </NavigationContainer>
    </NavigationIndependentTree>
  );
}

export { Drawer };
